import { useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useStore } from '../store/useStore'
import { useAuth } from '../contexts/AuthContext'

/**
 * チームプロジェクトとタスクのリアルタイム同期フック
 * Supabase Realtimeを使用してチームメンバー間でデータを同期
 */
export const useRealtimeSync = () => {
  const { user } = useAuth()
  const { fetchProjects, fetchTodos, selectedProjectId, notifyTodoFilesChanged, getLastManualUpdateAt } = useStore()

  // デバウンス用のタイマー参照
  const projectsDebounceTimer = useRef<NodeJS.Timeout | null>(null)
  const todosDebounceTimer = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!user) return

    console.log('[Realtime] Setting up realtime sync for user:', user.id)

    // デバウンス付きのfetchProjects
    const debouncedFetchProjects = () => {
      if (projectsDebounceTimer.current) {
        clearTimeout(projectsDebounceTimer.current)
      }
      projectsDebounceTimer.current = setTimeout(() => {
        // 手動更新の直後（2秒以内）であればスキップ（display_order更新を無視）
        const timeSinceManualUpdate = Date.now() - getLastManualUpdateAt()
        if (timeSinceManualUpdate < 2000) {
          // display_order更新中はログを出さない（ノイズ削減）
          return
        }
        console.log('[Realtime] Fetching projects after debounce')
        fetchProjects()
      }, 1000) // 1000ms待機（複数の更新をまとめる）
    }

    // デバウンス付きのfetchTodos
    const debouncedFetchTodos = () => {
      if (todosDebounceTimer.current) {
        clearTimeout(todosDebounceTimer.current)
      }
      todosDebounceTimer.current = setTimeout(() => {
        // 手動更新の直後（2秒以内）であればスキップ（display_order, gantt_sort_order更新を無視）
        const timeSinceManualUpdate = Date.now() - getLastManualUpdateAt()
        if (timeSinceManualUpdate < 2000) {
          // display_order, gantt_sort_order更新中はログを出さない（ノイズ削減）
          return
        }
        console.log('[Realtime] Fetching todos after debounce')
        if (selectedProjectId) {
          fetchTodos(selectedProjectId)
        } else {
          fetchTodos()
        }
      }, 500) // 500ms待機
    }

    // プロジェクトの変更を監視
    const projectsChannel = supabase
      .channel('projects_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE全て
          schema: 'public',
          table: 'projects'
        },
        (payload) => {
          // display_order更新以外のみログ出力（ノイズ削減）
          if (payload.eventType !== 'UPDATE' || !payload.new?.display_order) {
            console.log('[Realtime] Projects change detected:', payload.eventType)
          }
          // デバウンス付きで再取得
          debouncedFetchProjects()
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Projects channel status:', status)
      })

    // プロジェクト活動ログの変更を監視
    const activityLogsChannel = supabase
      .channel('activity_logs_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_activity_logs'
        },
        (payload) => {
          console.log('[Realtime] Activity logs change detected:', payload)
          // カスタムイベントを発火して、ProjectActivityLogコンポーネントに通知
          window.dispatchEvent(new CustomEvent('activity-log-change', { detail: payload }))
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Activity logs channel status:', status)
      })

    // タスクの変更を監視
    const todosChannel = supabase
      .channel('todos_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE全て
          schema: 'public',
          table: 'todos'
        },
        (payload) => {
          console.log('[Realtime] Todos change detected:', payload)
          // デバウンス付きで再取得
          debouncedFetchTodos()
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Todos channel status:', status)
      })

    // チームメンバーの変更を監視（メンバー追加・削除時）
    const teamMembersChannel = supabase
      .channel('team_members_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'team_members'
        },
        (payload) => {
          console.log('[Realtime] Team members change detected:', payload)
          // デバウンス付きで再取得（メンバー数が変わる可能性）
          debouncedFetchProjects()
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Team members channel status:', status)
      })

    // タスク画像の変更を監視
    const todoImagesChannel = supabase
      .channel('todo_images_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'todo_images'
        },
        (payload) => {
          console.log('[Realtime] Todo images change detected:', payload)
          // デバウンス付きで再取得して画像を更新
          debouncedFetchTodos()
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Todo images channel status:', status)
      })

    // タスクファイルの変更を監視
    const todoFilesChannel = supabase
      .channel('todo_files_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'todo_files'
        },
        (payload) => {
          console.log('[Realtime] Todo files change detected:', payload)
          // ファイル変更のタイムスタンプを更新（TodoItemコンポーネントで検知される）
          notifyTodoFilesChanged()
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Todo files channel status:', status)
      })

    // クリーンアップ: コンポーネントのアンマウント時にチャンネルを解除
    return () => {
      console.log('[Realtime] Cleaning up realtime channels')
      // タイマーをクリア
      if (projectsDebounceTimer.current) {
        clearTimeout(projectsDebounceTimer.current)
      }
      if (todosDebounceTimer.current) {
        clearTimeout(todosDebounceTimer.current)
      }
      // チャンネルを解除
      projectsChannel.unsubscribe()
      todosChannel.unsubscribe()
      teamMembersChannel.unsubscribe()
      todoImagesChannel.unsubscribe()
      todoFilesChannel.unsubscribe()
      activityLogsChannel.unsubscribe()
    }
  }, [user, selectedProjectId, fetchProjects, fetchTodos, notifyTodoFilesChanged, getLastManualUpdateAt])

  return null // このhookはUIを返さない
}
